import styled from "styled-components";
import React, { useEffect, useRef, useState } from "react";

import {
  TInvoice,
  TNexaasProduct,
} from "../../../../redux/reducers/globalState";

import Button, { TButtonIconVariants, TButtonProps } from "../../Button";
import TableGrid, {
  TArrayCell,
  TCell,
  TCellStyleVariants,
  TTableBody,
} from "../../TableGrid";
import TabsGroup from "../../../ui/groups/TabsGroup";
import { format } from "date-fns/format";
import apiService from "../../../../services/api";
import axios, { CancelTokenSource } from "axios";
import { parseISO } from "date-fns/parseISO";

const Container = styled.form`
  & > section:nth-child(2) {
    margin-top: 3px;
  }
`;

const Footer = styled.footer`
  margin-top: 24px;
  border-top: 1px solid var(--color-light-200);
  padding: 0px;
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;

  .textContainer {
    b,
    p {
      font-size: 14px;
      color: var(--color-text-paragraph);
    }

    b {
      font-family: var(--font-paragraph-bold);
    }

    p {
      font-family: var(--font-paragraph-default);
    }
  }

  .buttonsContainer {
    display: flex;
    justify-content: flex-end;

    & button:not(& button:last-child) {
      margin-right: 12px;
    }
  }
`;

export type TPayableObjectForm = {
  amount: string;
  category_id: number | null;
  classification_center_id: number | null;
  description: string;
  due_date: string;
  mode: null | "in_cash" | "recurrent" | "installments";
  supplier_id: number | null;

  paid_amount?: string;
  fine_amount?: string;
  interest_amount?: string;
  discount_amount?: string;
  payment_date?: string;
  period?:
    | "weekly"
    | "biweekly"
    | "monthly"
    | "bimonthly"
    | "quarterly"
    | "semiannual"
    | "annual";
  installments?: number;
  income_tax_relevant?: boolean;
};

type TGoodsProps = {
  data: {
    invoiceId: string;
    success: TNexaasProduct[];
    error: TNexaasProduct[];
  };
  footer: { buttons: TButtonProps<TButtonIconVariants>[] };
};

const GoodsCompleted: React.FC<TGoodsProps> = ({ data, footer }) => {
  const errorDataRef = useRef(data.error ?? []);
  const successDataRef = useRef(data.success ?? []);

  const hasOrderNumber = [...(data.error ?? []), ...(data.success ?? [])].some(
    (p) => p.order_number != null && p.order_number !== ""
  );

  const [isButtonLoading /* setIsButtonLoading */] = useState(
    Array.from({ length: 3 }, () => false)
  );

  const [isLoading, setIsLoading] = useState(true);

  const [shoulUpdateComponent /* setShoulUpdateComponent */] = useState(false);

  const [errorData, setErrorData] = useState<TNexaasProduct[]>([]);
  const [successData, setSuccessData] = useState<TNexaasProduct[]>([]);

  const [, /* invoice */ setInvoice] = useState<TInvoice | null>(null);

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const handleCheckNoSku = (data: TNexaasProduct[]) =>
    Array.isArray(data)
      ? data?.filter((value) =>
          /* value.sku === null */ value.reason?.includes("sku_not_found")
        )
      : [];

  const handleCheckNoBatchInfo = (data: TNexaasProduct[]) =>
    Array.isArray(data)
      ? data?.filter((value) =>
          /* !value.batch_expiration_date || !value.batch_manufacturing_date */ value.reason?.includes(
            "batch"
          )
        )
      : [];

  const handleCheckHasConversionFactor = (data: TNexaasProduct[]) =>
    Array.isArray(data)
      ? data?.filter((value) => value.conversion_factor !== null)
      : [];

  const onSubmitHandler = (onClick: ((props?: any) => void) | undefined) => {
    if (onClick) onClick();
  };

  const rowFormatter = (product: TNexaasProduct, index: number) => {
    const skuCell: TArrayCell<TCell<Exclude<TCellStyleVariants, "button">>> = [
      {
        style: "default",
        value: product.supplier_sku,
        marginRight: "8px",
        shouldBreakLine: true,
      },
      {
        style: "brokenLink",
        icon: "brokenLink",
        iconPosition: "left",
        value: "Não encontrado",
      },
    ];
    skuCell.forceFlex = true;

    const skuCellWithFlex = Object.assign([], skuCell, { forceFlex: true });

    return [
      {
        style: "default",
        value:
          product.index !== undefined && product.index !== null
            ? product.index.toString()
            : "",
      },
      ,
      // SKU/Cód. Fornecedor column
      product.sku === null
        ? skuCellWithFlex
        : product.sku === product.supplier_sku
        ? [
            {
              style: "default",
              value: product.sku,
              marginRight: "8px",
              shouldBreakLine: true,
            },
          ]
        : [
            {
              style: "default",
              value: product.sku,
              marginRight: "8px",
              shouldBreakLine: true,
            },
            {
              style: "default",
              value: product.supplier_sku,
              marginRight: "8px",
            },
          ],
      {
        style: "miscellaneous",
        values: [
          {
            style: "default",
            text: product.description,
            shouldBreakLine: true,
          },
          {
            style: "bold",
            text: "EAN:",
            shouldBreakLine: false,
            marginRight: "8px",
          },
          {
            style: "default",
            text: product.ean,
            shouldBreakLine: false,
          },
        ],
      },
      {
        style: "miscellaneous",
        values: [
          {
            style: "bold",
            text: product.quantity,
            shouldBreakLine: false,
            marginRight: "8px",
          },
          { style: "default", text: product.measure, shouldBreakLine: false },
        ],
      },
      [
        {
          style: "button",
          icon: "arrowRight",
          isDisabled: true,
          customCSS: { paddingLeft: 0 },
        },
      ],
      [
        {
          style: "miscellaneous",
          values: [
            {
              style: "bold",
              text:
                product.verified_quantity !== null
                  ? product.verified_quantity
                  : " - ",
              shouldBreakLine: false,
              marginRight: "8px",
            },
            {
              style: "default",
              text:
                product.verified_quantity !== null
                  ? product.verified_measure ?? "UN"
                  : "",
              marginRight: "8px",
              shouldBreakLine: false,
            },
          ],
        },
        product.conversion_factor !== null && {
          style: "button",
          icon: "exchange",
          tooltip: {
            customTextAlignment: "left",
            customTopPosition: 100 * index + 175,
            text: [
              {
                style: "default",
                text: "Fator de conversão:",
                shouldBreakLine: true,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.purchase_quantity,
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.purchase_measure,
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: "->",
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.sales_quantity,
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.sales_measure,
                shouldBreakLine: true,
              },
              {
                style: "default",
                text: "Para mudar o fator de conversão,",
                shouldBreakLine: true,
              },
              {
                style: "default",
                text: "acesse o cadastro do produto no OMS",
                shouldBreakLine: false,
              },
            ],
          },
          isDisabled: true,
        },
      ],
      ...(hasOrderNumber
        ? [
            {
              style: "miscellaneous",
              values: product.order_number
                ? [{ style: "default", text: product.order_number, shouldBreakLine: false }]
                : [{ style: "faded", text: "-", shouldBreakLine: false }],
            },
          ]
        : []),
      {
        style: "miscellaneous",
        values:
          !product.batch_name &&
          !product.batch_expiration_date &&
          !product.batch_manufacturing_date
            ? [{ style: "faded", text: "Sem lote", shouldBreakLine: false }]
            : [
                {
                  style: "bold",
                  text: product.batch_name ?? " - ",
                  shouldBreakLine: true,
                },
                {
                  style: "bold",
                  fontSize: "12px",
                  text: "Fab:",
                  marginRight: "3px",
                  shouldBreakLine: false,
                },
                {
                  style: "default",
                  fontSize: "12px",
                  text: product.batch_manufacturing_date
                    ? format(
                        parseISO(product.batch_manufacturing_date),
                        "dd/MM/yyyy"
                      )
                    : " - ",
                  marginRight: "3px",
                  shouldBreakLine: true,
                },
                {
                  style: "bold",
                  fontSize: "12px",
                  text: "Val:",
                  marginRight: "3px",
                  shouldBreakLine: false,
                },
                {
                  style: "default",
                  fontSize: "12px",
                  text: product.batch_expiration_date
                    ? format(
                        parseISO(product.batch_expiration_date),
                        "dd/MM/yyyy"
                      )
                    : " - ",
                  marginRight: "3px",
                  shouldBreakLine: true,
                },
              ],
      },
    ] as TTableBody<TCellStyleVariants>[];
  };

  useEffect(() => {
    setTimeout(() => {
      cancelTokenSource.current = axios.CancelToken.source();
      if (data.invoiceId) {
        setIsLoading(true);

        apiService
          .getInvoice(data.invoiceId, cancelTokenSource.current.token)
          .then((response: any) => {
            if (response && response.data) {
              setInvoice(response.data);
            } else {
              setInvoice(null); // or handle error state
            }
          })
          .catch(() => setInvoice(null))
          .finally(() => {
            setTimeout(() => {
              setIsLoading(false);
            }, 500);
          });
      }
    }, 500);

    if (errorData.length === 0 && successData.length === 0) {
      setErrorData(data.error);
      setSuccessData(data.success);
      errorDataRef.current = data.error ?? [];
      successDataRef.current = data.success ?? [];
    }

    return () => {
      if (cancelTokenSource.current)
        cancelTokenSource.current.cancel("from goods stock check, canceling");
    };
  }, []);

  // Calculate tab data and counts
  const tabData = [
    {
      label: "Todas",
      onClick: () => {
        setErrorData(errorDataRef.current);
        setSuccessData(successDataRef.current);
      },
      count: errorDataRef.current.length + successDataRef.current.length,
      content: (
        <>
          <h2>Todas</h2>
          <div></div>
        </>
      ),
      index: 0,
    },
    {
      label: "SKU não encontrado",
      onClick: () => {
        setErrorData(handleCheckNoSku(errorDataRef.current));
        setSuccessData(handleCheckNoSku(successDataRef.current));
      },
      count:
        handleCheckNoSku(errorDataRef.current).length +
        handleCheckNoSku(successDataRef.current).length,
      content: (
        <>
          <h2>SKU não encontrado</h2>
        </>
      ),
      index: 1,
    },
    {
      label: "Lote sem data preenchida",
      onClick: () => {
        setErrorData(handleCheckNoBatchInfo(errorDataRef.current));
        setSuccessData(handleCheckNoBatchInfo(successDataRef.current));
      },
      count:
        handleCheckNoBatchInfo(errorDataRef.current).length +
        handleCheckNoBatchInfo(successDataRef.current).length,
      content: (
        <>
          <h2>Lote sem data preenchida</h2>
          <div></div>
        </>
      ),
      index: 2,
    },
    {
      label: "Com fator de conversão",
      onClick: () => {
        setErrorData(handleCheckHasConversionFactor(errorDataRef.current));
        setSuccessData(handleCheckHasConversionFactor(successDataRef.current));
      },
      count:
        handleCheckHasConversionFactor(errorDataRef.current).length +
        handleCheckHasConversionFactor(successDataRef.current).length,
      content: (
        <>
          <h2>Com fator de conversão</h2>
          <div></div>
        </>
      ),
      index: 3,
    },
  ];

  // Filter out tabs with count === 0 (except "Todas", which should always show)
  const filteredTabs = tabData
    .filter((tab) => tab.count > 0)
    .map((tab, idx) => ({
      ...tab,
      index: idx,
    }));

  return (
    <Container>
      <TabsGroup
        key={shoulUpdateComponent + ""}
        initialActiveTabIndex={0}
        onClickItems={filteredTabs.map((tab) => tab.onClick)}
        stringIcon={filteredTabs
          .filter((_, idx) => idx !== 0) // skip "Todas" for icons if you want
          .map((tab) => {
            return {
              index: tab.index,
              value: tab.count.toString(),
            };
          })}
      >
        {filteredTabs.map((tab) => tab.content)}
      </TabsGroup>
      <TableGrid
        row="singleRow"
        borderColor={
          !isLoading
            ? [
                ...(errorData ?? [])
                  .sort((a, b) => a.index - b.index)
                  .map((error) =>
                    error.reason?.includes("sku_not_found") ||
                    error.reason?.includes("sku_batch_control")
                      ? "red"
                      : "light"
                  ),
                ...(successData ?? [])
                  .sort((a, b) => a.index - b.index)
                  .map(() => "light"),
              ]
            : undefined
        }
        columns={{
          tableHead: {
            width: [hasOrderNumber
              ? `0px 12px 50px 250px 300px 100px 50px 110px 80px 90px`
              : `0px 12px 50px 250px 360px 100px 50px 130px 90px`],
          },
          tableBody: {
            width: [hasOrderNumber
              ? `0px 12px 50px 250px 300px 100px 50px 110px 80px 90px`
              : `0px 12px 50px 250px 360px 100px 50px 130px 90px`],
          },
        }}
        onAction={() => {
          window.location.reload();
        }}
        emptyState={{
          isEmptyState:
            !isLoading && [...errorData, ...successData].length === 0,
          title: "Nenhuma nota encontrada",
          description: "Altere os filtros e realize a busca novamente",
        }}
        selectType={{
          type: "noSelect" /*, fullData: allInvoicesRef.current  */,
        }}
        tableHead={{
          customTopPosition: "-24px",
          customPaddingTop: "8px",
          value: [
            { style: "noIcon", showSkeleton: isLoading, value: "Nº" },
            {
              style: "noIcon",
              showSkeleton: isLoading,
              value: "SKU/Cód. Fornecedor",
            },
            { style: "noIcon", showSkeleton: isLoading, value: "Descrição" },
            { style: "noIcon", showSkeleton: isLoading, value: "Qtde." },
            {
              style: "noIcon",
              showSkeleton: isLoading,
              value: "",
            },
            {
              style: "noIcon",
              showSkeleton: isLoading,
              value: "Qtde. Conferida",
            },
            ...(hasOrderNumber
              ? [{ style: "noIcon" as const, showSkeleton: isLoading, value: "Nº Pedido" }]
              : []),
            { style: "noIcon", showSkeleton: isLoading, value: "Lote" },
          ],
        }}
        isLoading={isLoading}
        tableBody={
          isLoading
            ? new Array(5).fill("").map(() => ({
                elementId: "",
                file_nfe: null,
                file_xml: null,
                body: [
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "30px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "122px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "247px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "80px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "30px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      values: [
                        {
                          width: "100px",
                          style: {
                            overflow: "hidden",
                          },
                        },
                      ],
                    },
                  },
                  ...(hasOrderNumber
                    ? [
                        {
                          style: "default",
                          value: "",
                          skeleton: {
                            values: [{ width: "80px", style: { overflow: "hidden" } }],
                          },
                        },
                      ]
                    : []),
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      values: [
                        {
                          width: "90px",
                          style: {
                            overflow: "hidden",
                          },
                        },
                      ],
                    },
                  },
                ] as TTableBody<TCellStyleVariants>[],
              }))
            : [
                ...[...errorData]
                  .sort((a, b) => a.index - b.index)
                  .map((result, index: number) => ({
                    elementId: "",
                    file_nfe: "",
                    file_xml: "",
                    body: rowFormatter(result, index),
                  })),
                ...[...successData]
                  .sort((a, b) => a.index - b.index)
                  .map((result, index: number) => ({
                    elementId: "",
                    file_nfe: "",
                    file_xml: "",
                    body: rowFormatter(result, index),
                  })),
              ]
        }
      />
      <Footer>
        <div className="buttonsContainer">
          {footer.buttons?.map((buttonProps, index) => {
            const { onClick, ...props } = buttonProps;

            return (
              <Button
                key={index + " - " + isButtonLoading[index]}
                role={index === 2 ? "submit" : "button"}
                {...props}
                onClick={(e) => {
                  e.preventDefault();
                  onSubmitHandler(onClick);
                }}
                isLoading={isButtonLoading[index]}
              />
            );
          })}
        </div>
      </Footer>
    </Container>
  );
};

export default GoodsCompleted;
